import * as firebaseApp from "firebase/app";
import {
    getAuth,
    initializeAuth,
    onAuthStateChanged,
    browserLocalPersistence,
    indexedDBLocalPersistence,
    browserSessionPersistence,
    browserPopupRedirectResolver,
    type Persistence,
} from "firebase/auth";
import { startAuthStartupWatch } from "../lib/auth/authStartupWatch";
import { authStoresAtBoot, userStoredLocally, type AuthStoreName } from "../lib/auth/authStoreChoice";
import { AUTH_DB_NAME } from "../lib/auth/firebaseDatabaseNames";
import { boundHeartbeatHeader } from "../lib/auth/heartbeatHeaderBound";
import { demoIndustry } from "../demo/demoAddress";
import { demoFirebaseConfig } from "../demo/demoFirebaseConfig";

// The public demo (prest0.ai/demo/<industry>, src/demo/): its own project, which exists nowhere, so nothing on a demo
// page can reach Prest0's project and its signed-in user never shares a storage key with a real one.
const DEMO_INDUSTRY = demoIndustry();

// SECURITY: Firebase config from environment variables only - no hardcoded fallbacks
const firebaseConfig = DEMO_INDUSTRY ? demoFirebaseConfig(DEMO_INDUSTRY) : {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Validate required config before initializing
const requiredKeys = ['apiKey', 'authDomain', 'projectId'] as const;
for (const key of requiredKeys) {
    if (!firebaseConfig[key]) {
        console.error(`Missing required Firebase config: REACT_APP_FIREBASE_${key.toUpperCase().replace(/([A-Z])/g, '_$1').toUpperCase()}`);
    }
}

const app = firebaseApp.initializeApp(firebaseConfig);

/**
 * THE SIGNED-IN USER IS KEPT IN ONE PLACE, NAMED HERE (2026-09-23).
 *
 * Opening a second prest0.ai tab used to sign every other open tab out for
 * about a second. Measured on the live bundle: the first tab logged
 * `reset_session_begin reason: sign_out`, `signed_out`, then `signed_in`
 * again 0.8 s later. That sign-out ran the full reset in lib/auth/session.ts,
 * which clears the local thread copies, the composer draft and the other
 * per-account caches in the storage both tabs share.
 *
 * The cause was two opposite choices about where the user is stored.
 * `getAuth(app)` prefers the browser's database (IndexedDB) and moves a user
 * it finds in local storage there, deleting the local copy. session.ts then
 * called `setPersistence(browserLocalPersistence)`, which moved the user back.
 * So every page load moved the stored user out of local storage and then put
 * it back, with a network round trip in between. Every other tab watches that
 * local-storage key, saw it deleted, and signed out until it reappeared.
 *
 * So the order is stated once, at the app's birth, before anything can call
 * `getAuth()` (a caller needs this app, and this module creates it): local
 * storage first (where every signed-in user already is), then the browser's
 * database, then the per-tab session store for a browser without either. A
 * user found in the first store is never moved, so nothing any other tab
 * watches changes on boot. `getAuth()` everywhere else returns this instance.
 * The popup/redirect resolver is the same one `getAuth` supplies, so Google
 * sign-in by popup or redirect is unchanged.
 *
 * ONLY WHERE THE BROWSER BUILD IS LOADED. This import also reaches the SDK's
 * SERVER build: jest 27 ignores the package's `exports` map and loads its
 * `main` (`dist/node/index.js`), and so would any server-side import of this
 * module. There the popup/redirect resolver is an error object, not a class,
 * so `initializeAuth` refused it: the SDK logged `INTERNAL ASSERTION FAILED:
 * Expected a class definition` and threw into the catch below, in 45 unit
 * suites on every run. The server build has no browser storage to name, and
 * `getAuth()` serves it with its own defaults, as it did before this block
 * existed. In the browser build the resolver IS a class
 * (`BrowserPopupRedirectResolver`), so the call always runs there. Measured on
 * prest0.ai: a stored user stays in local storage and the browser database
 * holds none, which only this order produces.
 *
 * Proof: tests/auth/authStoreOrder.test.ts pins the order and the boot path,
 * tests/auth/authInitServerBuildIsQuiet.test.ts runs this module on the real
 * server build, and scripts/probe-second-tab-auth.mjs runs the real SDK in two
 * Chromium tabs. The probe shows the old order signing the first tab out and
 * this order leaving it signed in.
 *
 * NOTHING ON THE WAY TO THE FIRST ANSWER WAITS ON A BROWSER DATABASE (2026-10-31,
 * the sign-in stalls: about one page view in 22 waited 10 s or more for sign-in
 * to say who was here, half of them on Firebase's own database). Two changes:
 *   - someone already in local storage boots with local storage and the session
 *     store only, so Firebase never opens its browser database before it answers;
 *     nobody in local storage keeps the full order, so a record the SDK's old
 *     default left in the browser database is still found and moved
 *     (lib/auth/authStoreChoice.ts);
 *   - the heartbeat header every Firebase request waits for answers within
 *     200 ms, so its database cannot hold the boot lookup
 *     (lib/auth/heartbeatHeaderBound.ts).
 * Local storage stays first, so the never-moved property above still holds.
 * Proof: scripts/probe-auth-held-database.mjs runs the real SDK in Chromium with
 * each database held behind another tab's delete: the order this replaced and an
 * unbounded heartbeat wait past 4 s, this boot answers within the lookup.
 */
/**
 * The SDK's own component lookup, which hands out the heartbeat service: firebase/app exports it, and its public
 * types leave it out (the internal app.d.ts declares it).
 */
type ComponentLookup = (app: firebaseApp.FirebaseApp, name: 'heartbeat') => { getImmediate(options: { optional: true }): unknown };
const lookUpComponent = (firebaseApp as unknown as { _getProvider?: ComponentLookup })._getProvider;

const AUTH_STORES: Record<AuthStoreName, Persistence> = {
    local: browserLocalPersistence,
    indexedDB: indexedDBLocalPersistence,
    session: browserSessionPersistence,
};

function localStore(): Storage | null {
    try {
        return typeof window !== 'undefined' ? window.localStorage : null;
    } catch {
        return null;
    }
}

if (DEMO_INDUSTRY) {
    // On a demo page: the stored user the demo's boot seeded (src/demo/boot/isolate.ts), in the page's own storage,
    // and no popup or redirect sign-in (nobody signs in to the demo).
    try {
        initializeAuth(app, { persistence: [browserLocalPersistence] });
    } catch {
        // Already initialized (hot reload): getAuth(app) returns it.
    }
} else if (firebaseConfig.apiKey && typeof browserPopupRedirectResolver === 'function') {
    // The heartbeat first: the boot lookup below asks it for its header.
    try {
        if (typeof lookUpComponent === 'function') boundHeartbeatHeader(lookUpComponent(app, 'heartbeat').getImmediate({ optional: true }));
    } catch {
        /* no heartbeat service: nothing waits on one */
    }
    const storedLocally = userStoredLocally(firebaseConfig.apiKey, localStore());
    try {
        initializeAuth(app, {
            persistence: authStoresAtBoot(storedLocally).map((name) => AUTH_STORES[name]),
            popupRedirectResolver: browserPopupRedirectResolver,
        });
    } catch {
        // Already initialized (this module evaluated twice under hot reload):
        // getAuth(app) returns the existing instance, which carries this order.
    }
    // The sign-in start never waits in silence (2026-10-03): if it has not answered after 10 s and Firebase's own
    // database is held by another tab's pending delete, a notice says which step clears it (lib/auth/authStartupWatch.ts).
    // Only where there is a browser database to be held, and only the one this boot opens: none when the person is
    // already in local storage (the heartbeat's is bounded above).
    if (typeof window !== 'undefined' && typeof indexedDB !== 'undefined') {
        try {
            startAuthStartupWatch({
                onAnswer: (answered) => onAuthStateChanged(getAuth(app), (user) => answered(!!user)),
                databasesInUse: storedLocally ? [] : [AUTH_DB_NAME],
            });
        } catch {
            /* the watch must never affect the sign-in it watches */
        }
    }
}

export default app;